// The three PawBazaar websites. One account works on all of them; each has
// its own sign-in. Addresses come from REACT_APP_* (compiled in at build).
const clean = (u, d) => (u || d).replace(/\/+$/, "");
export const STORE_URL = clean(process.env.REACT_APP_STORE_URL, "https://pawbazaar.store");
export const CARE_URL = clean(process.env.REACT_APP_CARE_URL, "https://care.pawbazaar.store");
export const COMMUNITY_URL = clean(process.env.REACT_APP_COMMUNITY_URL, "https://community.pawbazaar.store");
export const CONSOLE_URL = clean(process.env.REACT_APP_CONSOLE_URL, "https://console.pawbazaar.store");

export const SITES = [
  { key: "shop", label: "Shop", url: STORE_URL, title: "Shop for your pet", line: "Food, treats, toys and more from verified shops near you, delivered the same day.", cta: "Start shopping" },
  { key: "care", label: "Care", url: CARE_URL, title: "Book pet care", line: "Grooming, spa, vet consultations and training at verified providers. Pick a time and pay online.", cta: "Explore services" },
  { key: "community", label: "Community", url: COMMUNITY_URL, title: "Adopt and help", line: "Adopt a pet, volunteer at drives, or donate what NGOs near you need.", cta: "Meet the NGOs" },
];

/** Where the signed-in customer's things live, across the three sites. */
export const ACCOUNT_LINKS = [
  { key: "orders", label: "Orders and bookings", site: "shop", path: "/account" },
  { key: "pets", label: "My pets", site: "shop", path: "/account?tab=pets" },
  { key: "wishlist", label: "Wishlist", site: "shop", path: "/account?tab=wishlist" },
  { key: "community", label: "My community", site: "community", path: "/account" },
  { key: "profile", label: "Profile and security", site: "shop", path: "/account?tab=profile" },
  { key: "help", label: "Help", site: "shop", path: "/account?tab=help" },
];
// Learn: step-by-step guides (How to) and pet care tips. It lives on the shop
// site (/learn) and sits after Community in the site switcher everywhere.
export const LEARN = { key: "learn", label: "Learn", url: `${STORE_URL}/learn` };
export const PET_CARE_TOPICS = [
  ["pet-guides", "Choosing and caring for a new pet"], ["health-tips", "Vaccines, ticks and common illnesses"],
  ["nutrition", "What and how much to feed"], ["behaviour", "Understanding your pet"], ["training", "Simple training at home"],
  ["travel-with-pets", "Travelling with your pet"],
];

export const siteUrl = (key) => (key === "console" ? CONSOLE_URL : SITES.find((s) => s.key === key).url);

export const PET_TYPES = ["Dogs", "Cats", "Birds", "Fish", "Rabbits", "Small Pets"];

/** Each site's own menu row under the header (computers). The header's small
 *  Shop · Care · Community switcher moves between sites; this row only holds
 *  the current site's things. On the shop, a pet opens a panel of that pet's
 *  categories (`pet`) and Brands opens the brands sold nearby (`brands`).
 *  {label, site, path} */
const pet = (p) => ({ label: p === "Small Pets" ? "Small pets" : p, site: "shop", path: `/shop?pet_type=${encodeURIComponent(p)}`, pet: p });
export const SITE_MENUS = {
  shop: [
    ...PET_TYPES.map(pet),
    { label: "Offers", site: "shop", path: "/shop?offers=1", deal: true, gap: true },
    { label: "Brands", site: "shop", path: "/shop", brands: true },
    { label: "Buy again", site: "shop", path: "/account" },
    { label: "Subscribe and save", site: "shop", path: "/shop?subscription=1" },
  ],
  // The care services come from the admin's list on the Care site (MegaNav useCareMenu); these until it loads.
  care: [
    { label: "Grooming", site: "care", path: "/services?group=grooming", group: "grooming" },
    { label: "Vet and vaccinations", site: "care", path: "/services?group=vet", group: "vet" },
    { label: "Training", site: "care", path: "/services?group=training", group: "training" },
    { label: "Day care", site: "care", path: "/services?group=daycare", group: "daycare" },
    { label: "Dog walking", site: "care", path: "/services?group=walking", group: "walking" },
    { label: "Boarding", site: "care", path: "/services?group=boarding", group: "boarding" },
    { label: "Pet sitting", site: "care", path: "/services?group=sitting", group: "sitting" },
    { label: "All services", site: "care", path: "/services" },
    { label: "My bookings", site: "care", path: "/account", gap: true },
  ],
  community: [
    { label: "Adopt a pet", site: "community", path: "/adopt" },
    { label: "NGOs", site: "community", path: "/ngos" },
    { label: "Donate", site: "community", path: "/campaigns" },
    { label: "Drives and events", site: "community", path: "/drives" },
    { label: "My community", site: "community", path: "/account", gap: true },
  ],
};

// Pages planned but not built yet (Learn articles, policies, more services).
// Links to them open a friendly "Coming soon" page on the shop site
// (/soon/<slug>) until the real page exists; then point the link at it.
export const SOON = {
  "pet-guides": "Pet guides", "health-tips": "Health tips", nutrition: "Nutrition", behaviour: "Behaviour",
  training: "Training", "travel-with-pets": "Travel with pets", "expert-articles": "Expert articles",
  boarding: "Boarding", "pet-walking": "Pet walking", "pet-sitting": "Pet sitting", vaccinations: "Vaccinations",
  "emergency-care": "Emergency care", "pet-groups": "Pet groups", "lost-and-found": "Lost and found",
  "pet-friendly-places": "Pet-friendly places", discussions: "Discussions", "pet-stories": "Pet stories",
  offers: "Offers and deals", "returns-refunds": "Returns and refunds", shipping: "Shipping policy",
  "seller-support": "Seller support", faq: "FAQ", contact: "Contact us", privacy: "Privacy policy", "cookie-policy": "Cookie policy",
  terms: "Terms of service", "refund-policy": "Refund and return policy", "seller-policy": "Seller policy",
  "app-store": "PawBazaar on the App Store", "google-play": "PawBazaar on Google Play",
};
const soon = (slug, icon) => ({ label: SOON[slug], site: "shop", path: `/soon/${slug}`, icon, soon: true });
// Help and policy pages on the shop site (CUS-2).
const page = (slug, path) => ({ label: SOON[slug], site: "shop", path });

/** The footer on all three sites (same everywhere). Only pages that exist:
 *  planned pages join the footer once they are built. */
const shopPet = (label, pet) => ({ label, site: "shop", path: `/shop?pet_type=${encodeURIComponent(pet)}` });
export const FOOTER = {
  tagline: "For happier, healthier pets",
  intro: "Everything your pet needs: shop from shops near you, book care, and be part of a loving community.",
  columns: [
    { title: "Shop", items: [shopPet("Dog products", "Dogs"), shopPet("Cat products", "Cats"), shopPet("Bird products", "Birds"),
      shopPet("Small pets", "Small Pets"), shopPet("Fish products", "Fish"), { label: "All products", site: "shop", path: "/shop" },
      { label: "Offers and deals", site: "shop", path: "/shop?offers=1" }] },
    { title: "Care", items: [{ label: "Vet and vaccinations", site: "care", path: "/services?group=vet" }, { label: "Grooming", site: "care", path: "/services?group=grooming" },
      { label: "Training", site: "care", path: "/services?group=training" }, { label: "All services", site: "care", path: "/services" }] },
    { title: "Community", items: [{ label: "Adoption", site: "community", path: "/adopt" }, { label: "Drives and events", site: "community", path: "/drives" },
      { label: "Verified NGOs", site: "community", path: "/ngos" }, { label: "Donate", site: "community", path: "/campaigns" }] },
    { title: "Learn", items: [{ label: "How-to guides", site: "shop", path: "/learn" }, { label: "Guides for businesses", site: "shop", path: "/learn?for=business" },
      { label: "Pet care tips", site: "shop", path: "/learn?tab=pet-care" }] },
    { title: "Help", items: [{ label: "Track order", site: "shop", path: "/account" }, page("returns-refunds", "/policies/refunds"),
      page("shipping", "/policies/shipping"), { label: "Sell on PawBazaar", site: "console", path: "/join/shop" },
      page("seller-support", "/sellers"), { label: "Help centre and FAQ", site: "shop", path: "/help-centre" }, page("contact", "/contact"),
      { label: "Partner and staff login", site: "console", path: "/login" }] },
  ],
  trust: [["shield-check", "Secure payments"], ["badge-check", "Genuine products"], ["heart-handshake", "Trusted service providers"], ["rotate-ccw", "Easy returns"]],
  legal: [page("privacy", "/policies/privacy"), page("cookie-policy", "/policies/cookies"), page("terms", "/policies/terms"), page("refund-policy", "/policies/refunds"),
    page("seller-policy", "/policies/sellers"), page("contact", "/contact")],
  apps: [soon("app-store"), soon("google-play")],
  // Fallback order; the real addresses come from Admin › Settings › Company and contact
  // (/api/site-info) and an icon only shows once its address is set.
  socials: [["instagram", "Instagram", "https://instagram.com"], ["facebook", "Facebook", "https://facebook.com"],
    ["youtube", "YouTube", "https://youtube.com"], ["linkedin", "LinkedIn", "https://linkedin.com"], ["x", "X", "https://x.com"]],
};
